import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, Grid, Heading, Text, Select, Input, InputGroup, InputLeftElement, Icon, IconButton, Spinner, Checkbox,
  Button, useToast,
} from '@chakra-ui/react';
import { FiSearch, FiEye, FiTrash2 } from 'react-icons/fi';
import useAuth from 'hooks/useAuth';
import {
  getHistoricalOptions, getHistoricalSummary, getHistoricalStudents, deleteHistoricalStudent, deleteHistoricalStudents,
  READ_ROLES, EDIT_ROLES, SOURCE_LABEL, SOURCE_TONE, STATUS, REVIEW_STATUS,
} from 'api/administrador/historicalBase';
import { KpiCard, StatusPill } from 'components/monitoring/ai/common';
import AiPagination from 'components/monitoring/ai/AiPagination';
import StudentDrawer from 'components/historical-base/StudentDrawer';
import RecordModal from 'components/historical-base/RecordModal';

const PAGE_SIZE = 20;
const CONFIRM_VALUES = { reason: '' };
// Con edición: casilla al inicio y dos acciones (ver, eliminar) al final.
const COLS_READ = '2.2fr 1.1fr 1fr 1fr 0.8fr 1.1fr 56px';
const COLS_EDIT = '36px 2.2fr 1.1fr 1fr 1fr 0.8fr 1.1fr 92px';
const DELETE_HELP = 'Se quitan de la Base histórica junto con sus notas y pagos. No se borran de la base de datos: queda en Auditoría con quién y por qué, y si se vuelve a importar el archivo no reaparecen.';
const DELETE_HELP_ONE = 'Se quita de la Base histórica junto con sus notas y pagos. No se borra de la base de datos: queda en Auditoría con quién y por qué, y si se vuelve a importar el archivo no reaparece.';

function StatusDot({ status }) {
  const s = STATUS[status];
  if (!s) return <Text fontSize="sm" color="gray.400">—</Text>;
  return (
    <Flex align="center" gap={2} fontSize="sm" whiteSpace="nowrap">
      <Box w="8px" h="8px" borderRadius="999px" bg={s.color} flexShrink={0} />
      {s.label}
    </Flex>
  );
}

function GradesCell({ grades }) {
  if (!grades?.total) return <Text fontSize="sm" color="gray.400">Sin notas</Text>;
  if (!grades.empty) return <Text fontSize="sm" color="gray.700">{grades.total} completas</Text>;
  return (
    <Text fontSize="sm" color="gray.700" whiteSpace="nowrap">
      {grades.total} · <Text as="span" color="orange.500" fontWeight="600">{grades.empty} {grades.empty === 1 ? 'vacía' : 'vacías'}</Text>
    </Text>
  );
}

export default function BaseHistorica() {
  const { user } = useAuth();
  const role = user?.role?.type;
  const isAllowed = READ_ROLES.includes(role);
  const canEdit = EDIT_ROLES.includes(role);

  const [filters, setFilters] = useState({ source: '', status: '', search: '', emptyGrades: false, page: 1 });
  const [searchText, setSearchText] = useState('');
  const [options, setOptions] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null);
  const toast = useToast();
  const COLS = canEdit ? COLS_EDIT : COLS_READ;

  useEffect(() => {
    if (!isAllowed) return;
    getHistoricalOptions().then(setOptions).catch(() => setOptions(null));
  }, [isAllowed]);

  // Búsqueda con pequeña espera para no consultar en cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchText ? f : { ...f, search: searchText, page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchText]);

  const base = useMemo(
    () => ({ source: filters.source, status: filters.status, search: filters.search, emptyGrades: filters.emptyGrades ? 'true' : '' }),
    [filters.source, filters.status, filters.search, filters.emptyGrades],
  );

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      const [s, list] = await Promise.all([
        getHistoricalSummary(base),
        getHistoricalStudents({ ...base, page: filters.page, pageSize: PAGE_SIZE }),
      ]);
      setSummary(s);
      setRows(list?.data || []);
      setPagination(list?.pagination || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[base histórica] error cargando estudiantes:', e);
    } finally {
      setLoading(false);
    }
  }, [base, filters.page]);

  useEffect(() => { if (isAllowed) load(false); }, [isAllowed, load]);

  // La selección es de la página visible: al cambiar filtros o página se limpia (nunca se elimina algo que no se ve).
  useEffect(() => { setSelected(new Set()); }, [base, filters.page]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.id)));

  const fullName = (r) => [r.firstNames, r.lastNames].filter(Boolean).join(' ') || `código ${r.sourceCode}`;

  const askDeleteOne = (r) => setConfirm({
    title: 'Eliminar estudiante',
    description: `«${fullName(r)}» (${SOURCE_LABEL[r.source]}). ${DELETE_HELP_ONE}`,
    onSubmit: async (v) => {
      await deleteHistoricalStudent(r.id, v);
      toast({ status: 'success', title: 'Estudiante eliminado de la Base histórica', duration: 3000, isClosable: true });
      load(true);
    },
  });

  const askDeleteSelected = () => {
    const ids = [...selected];
    setConfirm({
      title: `Eliminar ${ids.length} ${ids.length === 1 ? 'estudiante' : 'estudiantes'}`,
      description: DELETE_HELP,
      onSubmit: async (v) => {
        const res = await deleteHistoricalStudents(ids, v.reason);
        toast({ status: 'success', title: `${res.deleted} ${res.deleted === 1 ? 'estudiante eliminado' : 'estudiantes eliminados'} de la Base histórica`, duration: 3000, isClosable: true });
        setSelected(new Set());
        load(true);
      },
    });
  };

  if (!isAllowed) {
    return (
      <Box p={8}>
        <Text color="gray.500">Esta vista está disponible solo para supervisión.</Text>
      </Box>
    );
  }

  const statuses = options?.statuses || Object.keys(STATUS);
  const sources = options?.sources || Object.keys(SOURCE_LABEL);

  return (
    <Box p={{ base: 3, md: 6 }}>
      <Box mb={5}>
        <Heading fontSize="22px" fontWeight="700" letterSpacing="-.01em">Base histórica</Heading>
        <Text color="gray.500" fontSize="14px" mt={1}>
          Estudiantes de las plataformas anteriores de Klazia. Busca por documento o nombre, revisa su ficha
          {canEdit ? ' y completa notas y pagos.' : '.'}
        </Text>
      </Box>

      <Flex align="center" gap={3} mb={4} wrap="wrap">
        <Text fontSize="11px" fontWeight="700" letterSpacing=".05em" color="gray.500" textTransform="uppercase">Filtrar por</Text>
        <Select
          aria-label="Origen" w={{ base: '100%', sm: '220px' }} bg="white" borderRadius="8px"
          value={filters.source}
          onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value, page: 1 }))}
        >
          <option value="">Todos los orígenes</option>
          {sources.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s] || s}</option>)}
        </Select>
        <Select
          aria-label="Estado" w={{ base: '100%', sm: '220px' }} bg="white" borderRadius="8px"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
        >
          <option value="">Todos los estados</option>
          {statuses.map((s) => <option key={s} value={s}>{STATUS[s]?.label || s}</option>)}
          <option value={REVIEW_STATUS.value}>{REVIEW_STATUS.label}</option>
        </Select>
      </Flex>

      <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, minmax(0, 1fr))' }} gap={3} mb={5}>
        <KpiCard loading={!summary} label="Estudiantes" value={(summary?.students ?? 0).toLocaleString('es-CO')} foot="según los filtros" color="#574feb" />
        <KpiCard loading={!summary} label="Graduados" value={(summary?.graduated ?? 0).toLocaleString('es-CO')} foot="pueden pedir constancia" color="#10B981" />
        <KpiCard
          loading={!summary} label="Notas vacías" value={(summary?.emptyGrades ?? 0).toLocaleString('es-CO')} color="#F59E0B"
          foot={`en ${(summary?.studentsWithEmptyGrades ?? 0).toLocaleString('es-CO')} estudiantes`}
        />
        <KpiCard loading={!summary} label="Correos dudosos" value={(summary?.doubtfulEmails ?? 0).toLocaleString('es-CO')} foot="excluir de envíos masivos" color="#F59E0B" />
      </Grid>

      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px">
        <Flex px={5} py={4} gap={4} align="center" wrap="wrap">
          <InputGroup w={{ base: '100%', md: '360px' }}>
            <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color="gray.400" /></InputLeftElement>
            <Input
              aria-label="Buscar por documento o nombre"
              placeholder="Documento o nombre"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              borderRadius="8px"
            />
          </InputGroup>
          <Checkbox
            colorScheme="purple" isChecked={filters.emptyGrades}
            onChange={(e) => setFilters((f) => ({ ...f, emptyGrades: e.target.checked, page: 1 }))}
          >
            <Text fontSize="sm" color="gray.600">Con notas vacías</Text>
          </Checkbox>
        </Flex>

        {canEdit && selected.size > 0 && (
          <Flex px={5} py={2.5} gap={3} align="center" wrap="wrap" bg="red.50" borderTop="1px solid" borderColor="red.100">
            <Text fontSize="sm" fontWeight="600" color="red.700">
              {selected.size} {selected.size === 1 ? 'seleccionado' : 'seleccionados'}
            </Text>
            <Button size="sm" colorScheme="red" leftIcon={<FiTrash2 />} onClick={askDeleteSelected}>Eliminar seleccionados</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Quitar selección</Button>
          </Flex>
        )}

        <Box overflowX="auto">
          <Box minW="860px">
            <Grid templateColumns={COLS} px={5} py={3} bg="gray.50" fontSize="11px" fontWeight="600" letterSpacing=".06em" color="gray.600" textTransform="uppercase">
              {canEdit && (
                <Checkbox
                  aria-label="Seleccionar todos los de esta página" colorScheme="purple" isChecked={allOnPage}
                  isIndeterminate={!allOnPage && rows.some((r) => selected.has(r.id))} onChange={toggleAll}
                />
              )}
              <Text>Estudiante</Text><Text>Origen</Text><Text>Estado</Text><Text>Programa</Text><Text>Correo</Text><Text>Notas</Text><Text />
            </Grid>

            {loading && rows.length === 0 && (
              <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>
            )}
            {!loading && rows.length === 0 && (
              <Text textAlign="center" py={10} color="gray.500" fontSize="sm">
                No hay estudiantes con esos filtros. Prueba con otro documento o quita filtros.
              </Text>
            )}
            {rows.map((r) => (
              <Grid
                key={r.id} templateColumns={COLS} px={5} py={3} alignItems="center" borderTop="1px solid" borderColor="gray.100"
                cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => setOpenId(r.id)}
                bg={selected.has(r.id) ? 'purple.50' : undefined}
              >
                {canEdit && (
                  <Box onClick={(e) => e.stopPropagation()}>
                    <Checkbox aria-label={`Seleccionar a ${fullName(r)}`} colorScheme="purple" isChecked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                  </Box>
                )}
                <Box minW={0}>
                  <Flex align="center" gap={2} minW={0}>
                    <Text fontSize="sm" fontWeight="600" isTruncated>{[r.firstNames, r.lastNames].filter(Boolean).join(' ') || 'Sin nombre'}</Text>
                    {r.reviewReason && <Box flexShrink={0} title={r.reviewReason}><StatusPill label="Por revisar" tone="orange" size="10px" /></Box>}
                  </Flex>
                  <Text fontSize="xs" color="gray.500" fontFamily="mono" isTruncated>{[r.documentType, r.document].filter(Boolean).join(' ') || 'Sin documento'}</Text>
                </Box>
                <Box><StatusPill label={SOURCE_LABEL[r.source] || r.source} tone={SOURCE_TONE[r.source]} /></Box>
                <StatusDot status={r.status} />
                <Text fontSize="sm" color="gray.700" isTruncated>{r.program || '—'}</Text>
                <Text fontSize="sm" fontWeight="500" color={r.emailStatus === 'dudoso' ? 'orange.500' : 'green.600'}>
                  {r.emailStatus === 'dudoso' ? 'Dudoso' : 'Válido'}
                </Text>
                <GradesCell grades={r.grades} />
                <Flex justify="flex-end" gap={1.5}>
                  <IconButton
                    aria-label="Ver ficha del estudiante" icon={<FiEye />} size="sm" variant="outline" borderRadius="8px"
                    onClick={(e) => { e.stopPropagation(); setOpenId(r.id); }}
                  />
                  {canEdit && (
                    <IconButton
                      aria-label={`Eliminar a ${fullName(r)}`} icon={<FiTrash2 />} size="sm" variant="outline" colorScheme="red" borderRadius="8px"
                      onClick={(e) => { e.stopPropagation(); askDeleteOne(r); }}
                    />
                  )}
                </Flex>
              </Grid>
            ))}
          </Box>
        </Box>

        <AiPagination
          pagination={pagination}
          noun="estudiantes"
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
        />
      </Box>

      <StudentDrawer
        studentId={openId}
        isOpen={!!openId}
        onClose={() => setOpenId(null)}
        canEdit={canEdit}
        options={options}
        onChanged={() => load(true)}
        onDeleted={() => { setOpenId(null); load(true); }}
      />

      {confirm && (
        <RecordModal
          isOpen
          onClose={() => setConfirm(null)}
          title={confirm.title}
          description={confirm.description}
          fields={[{ name: 'reason', label: 'Motivo (opcional)', placeholder: 'Ej. registro sin nombre, posible bot', full: true }]}
          initialValues={CONFIRM_VALUES}
          submitLabel="Eliminar"
          danger
          onSubmit={confirm.onSubmit}
        />
      )}
    </Box>
  );
}
